import * as React from "react";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2Icon } from "lucide-react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-transparent bg-clip-padding text-sm font-medium leading-none transition-all outline-none select-none focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90 disabled:hover:bg-primary",
        destructive:
          "bg-destructive text-white shadow-xs hover:bg-destructive/90 disabled:hover:bg-destructive focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border-border bg-background shadow-xs hover:bg-muted hover:text-foreground disabled:hover:bg-background disabled:hover:text-current aria-expanded:bg-muted aria-expanded:text-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50 dark:disabled:hover:bg-input/30 [&_svg]:text-foreground [&_svg]:hover:text-foreground",
        outlineDestructive:
          "border-destructive bg-background text-destructive shadow-xs hover:bg-red-100 disabled:hover:bg-background disabled:hover:text-destructive focus-visible:ring-destructive/20 dark:border-destructive dark:hover:bg-destructive dark:hover:text-white dark:disabled:hover:bg-background dark:focus-visible:ring-destructive/40",
        outlineWarning:
          "border-warning-700 bg-background text-warning-700 shadow-xs hover:bg-warning-100 disabled:hover:bg-background disabled:hover:text-warning-700 focus-visible:ring-warning-500/20 dark:border-warning-500 dark:hover:bg-warning-500/10 dark:disabled:hover:bg-background dark:focus-visible:ring-warning-500/40",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 disabled:hover:bg-secondary aria-expanded:bg-secondary aria-expanded:text-secondary-foreground [&_svg]:text-secondary-foreground",
        ghost:
          "hover:bg-muted hover:text-foreground disabled:hover:bg-transparent disabled:hover:text-current aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-muted/50 [&_svg]:text-foreground [&_svg]:hover:text-foreground",
        ghostDestructive:
          "text-destructive hover:border-red-100 hover:bg-red-100 disabled:hover:bg-transparent disabled:hover:border-transparent [&_svg]:text-destructive",
        ghostWarning:
          "text-warning-700 hover:border-warning-100 hover:bg-warning-100 disabled:hover:bg-transparent disabled:hover:border-transparent [&_svg]:text-warning-700",
        link: "text-primary underline-offset-4 hover:underline disabled:hover:no-underline [&_svg]:text-foreground",
        subtle:
          "bg-transparent text-muted-foreground hover:bg-transparent hover:text-primary",
      },
      size: {
        default: "h-8 gap-1.5 px-2.5 py-1.5",
        xs: "h-6 gap-1 px-2 py-1 text-xs [&_svg:not([class*='size-'])]:size-3",
        sm: "h-7 gap-1 px-2.5 py-1.5 text-xs [&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-9 gap-1.5 px-4",
        iconxs: "size-6 [&_svg:not([class*='size-'])]:size-3",
        iconsm: "size-7",
        icon: "size-8",
        iconlg: "size-9",
        iconxl:
          "size-10 [&>svg:not(.loading)]:h-6 [&>svg:not(.loading)]:w-6 [&>svg:not(.loading)]:stroke-[1.25]",
      },
      loading: {
        true: "cursor-not-allowed!",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isDisabled?: boolean;
  loadingText?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      children,
      asChild = false,
      loading = false,
      disabled = false,
      isDisabled = false,
      loadingText,
      type = "button",
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";

    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, className, loading }),
          loading && "[&_svg:not(.loading)]:hidden",
          isDisabled || disabled ? "cursor-not-allowed" : "cursor-pointer"
        )}
        ref={ref}
        disabled={loading || disabled || isDisabled}
        type={type}
        {...props}
      >
        {loading && <Loader2Icon className="text-muted animate-spin loading" />}
        <Slottable>{loading && loadingText ? loadingText : children}</Slottable>
      </Comp>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
